<template>
    <div class="container is-max-desktop">
        <nav class="navbar" role="navigation" aria-label="main navigation">
            <div class="navbar-brand">
                <a class="navbar-item" href="#" alt="Timist Blocks">
                    <svg width="598" height="667" viewBox="0 0 598 667" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd"
                            d="M598 0V170.769H384.857V666.385H214V170.769H0V0H598ZM512.571 666C559.752 666 598 627.772 598 580.615C598 533.459 559.752 495.231 512.571 495.231C465.391 495.231 427.143 533.459 427.143 580.615C427.143 627.772 465.391 666 512.571 666Z"
                            fill="#D8D8D8" />
                        <mask id="mask0_0_2317" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0"
                            width="598" height="667">
                            <path fill-rule="evenodd" clip-rule="evenodd"
                                d="M598 0V170.769H384.857V666.385H214V170.769H0V0H598ZM512.571 666C559.752 666 598 627.772 598 580.615C598 533.459 559.752 495.231 512.571 495.231C465.391 495.231 427.143 533.459 427.143 580.615C427.143 627.772 465.391 666 512.571 666Z"
                                fill="white" />
                        </mask>
                        <g mask="url(#mask0_0_2317)">
                            <rect x="-222" y="-188" width="1024" height="1024" fill="url(#paint0_linear_0_2317)" />
                        </g>
                        <defs>
                            <linearGradient id="paint0_linear_0_2317" x1="-90.9837" y1="320.955" x2="286.489"
                                y2="793.775" gradientUnits="userSpaceOnUse">
                                <stop stop-color="#1E8E67" />
                                <stop offset="1" stop-color="#352F94" />
                            </linearGradient>
                        </defs>
                    </svg>
                    <span class="brand-wordmark ml-3">
                        <span class="title is-3 mb-0">Timist</span>
                        <span v-if="isPlusUser" class="brand-plus title is-3 mb-0">Plus</span>
                        <span v-else-if="isProUser" class="brand-pro title is-3 mb-0">PRO</span>
                    </span>
                </a>
                <span class="tag ml-2 is-align-self-center is-hidden-touch">Preview</span>
                <TodaySummary class="navbar-brand-summary is-hidden-desktop" variant="compact" />
            </div>
            <div id="navbarBasicExample" class="navbar-menu">
                <div class="navbar-start">
                </div>
                <div class="navbar-end">
                    <TodaySummary class="is-hidden-touch" />
                    <div v-if="!isFrontendOnly && isFreeUser" class="navbar-item">
                        <button class="button is-small is-rounded" @click="showUpgradeModal = true">
                            Upgrade
                        </button>
                    </div>
                    <a v-if="!isFrontendOnly" class="navbar-item" :class="{ 'is-loading-feedback': feedbackLoading }" @click="featurecat">
                        <FontAwesomeIcon :icon="feedbackLoading ? faSpinner : faChevronCircleUp" :spin="feedbackLoading" />
                        Feedback
                    </a>
                    <a v-if="!isFrontendOnly" class="navbar-item is-hidden-mobile" @click="showContactModal = true" title="Contact Support">
                        <FontAwesomeIcon :icon="faCircleQuestion" />
                    </a>
                    <ProfileDropdown v-if="!isFrontendOnly" />
                </div>
            </div>
        </nav>
        <ContactModal :isOpen="showContactModal" @close="showContactModal = false" />
        <UpgradeModal :isOpen="showUpgradeModal" @close="showUpgradeModal = false" />
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import ProfileDropdown from './ProfileDropdown.vue';
import ContactModal from './ContactModal.vue';
import UpgradeModal from './UpgradeModal.vue';
import TodaySummary from './TodaySummary.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronCircleUp, faCircleQuestion, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { storeToRefs } from 'pinia';
import { useBlocksStore } from '../stores/blocks';

// Hide profile dropdown in local/frontend-only mode
const store = useBlocksStore();
const { isFrontendOnly, userSession } = storeToRefs(store);

const showContactModal = ref(false);
const showUpgradeModal = ref(false);
const feedbackLoading = ref(false);
const isPlusUser = computed(() => userSession.value?.plan === 'plus');
const isProUser = computed(() => userSession.value?.plan === 'pro');
const isFreeUser = computed(() => !userSession.value?.plan);

const featurecat = async () => {
    if (feedbackLoading.value) return;
    feedbackLoading.value = true;
    try {
        const response = await fetch('/api/featurecat');
        if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        if (data.url) {
            window.location.href = data.url;
            return; // keep spinner up through navigation
        }
        throw new Error('No feedback URL returned');
    } catch (error) {
        console.error('Error fetching featurecat URL:', error);
        feedbackLoading.value = false;
    }
}

// The success path keeps the spinner up while the browser navigates away.
// On back/forward (incl. bfcache restore) the page is shown again with state
// intact, so clear the flag on pageshow to avoid a stuck spinner.
const resetFeedback = () => { feedbackLoading.value = false; };
onMounted(() => window.addEventListener('pageshow', resetFeedback));
onBeforeUnmount(() => window.removeEventListener('pageshow', resetFeedback));
</script>

<style>
.is-loading-feedback {
    opacity: 0.7;
    cursor: progress;
}

.brand-wordmark {
    display: inline-flex;
    align-items: baseline;
    gap: 0.28rem;
    min-width: 0;
}

.navbar-brand-summary {
    margin-left: auto;
}

.brand-plus {
    display: inline-block;
    font-weight: 600;
    line-height: 1;
    transform: translateY(-0.01em);
    text-transform: none;
}

@supports (-webkit-text-stroke: 1px currentColor) {
    .brand-plus {
        -webkit-text-fill-color: transparent;
        -webkit-text-stroke: 1.7px currentColor;
    }
}

.brand-pro {
    display: inline-block;
    font-family: ui-monospace, "SF Mono", "IBM Plex Mono", "JetBrains Mono", monospace;
    font-weight: 950;
    font-size: 1.18em;
    letter-spacing: -0.015em;
    line-height: 1;
    margin-left: 0.1em;
    position: relative;
    text-transform: uppercase;
    transform: scaleX(1.18);
    transform-origin: left center;
}

@media screen and (max-width: 1023px) {
    .navbar-brand {
        align-items: stretch;
        width: 100%;
    }

    .navbar-brand-summary {
        flex-shrink: 0;
    }
}

@media screen and (max-width: 768px) {
    .brand-wordmark {
        gap: 0.18rem;
    }

    .brand-plus {
        transform: translateY(-0.01em);
    }

    .brand-pro {
        font-size: 1.14em;
        letter-spacing: -0.01em;
        transform: translateY(0.01em) scaleX(1.12);
    }
}

</style>